import React, { useState } from 'react';
import { Send, Building2, User, Mail, Phone, MapPin, Calendar, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIONES_CHILE = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', "O'Higgins", 'Maule', 'Ñuble', 'Biobío',
  'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes'
];

const TIPOS_INSTITUCION = [
  { value: 'universidad', label: 'Universidad' },
  { value: 'hospital', label: 'Hospital / Clínica' },
  { value: 'instituto', label: 'Instituto / Centro de Formación' },
  { value: 'cesfam', label: 'CESFAM / APS' },
  { value: 'red_salud', label: 'Red de Salud' },
  { value: 'otro', label: 'Otro' }
];

const COMO_CONOCIO = [
  'Búsqueda en Google',
  'Redes sociales',
  'Recomendación de colega',
  'Evento o congreso',
  'Otro'
];

export default function QuoteRequestPage({ 
  preselectedPlan = null,
  onClose = null,
  isModal = false 
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  
  const [formData, setFormData] = useState({
    // Contact info
    nombre_contacto: '',
    email_contacto: '',
    telefono_contacto: '',
    cargo: '',
    // Institution info
    nombre_institucion: '',
    tipo_institucion: '',
    rut_institucion: '',
    region: '',
    comuna: '',
    sitio_web: '',
    // Requirements
    plan_interes: preselectedPlan || '',
    num_usuarios_estimado: '',
    num_establecimientos: '',
    num_instructores: '',
    num_alumnos: '',
    escenarios_personalizados: false,
    num_escenarios_personalizados: '',
    descripcion_escenarios: '',
    // Additional
    comentarios: '',
    como_conocio: '',
    fecha_inicio_deseada: ''
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        num_usuarios_estimado: formData.num_usuarios_estimado ? parseInt(formData.num_usuarios_estimado) : null,
        num_establecimientos: formData.num_establecimientos ? parseInt(formData.num_establecimientos) : null,
        num_instructores: formData.num_instructores ? parseInt(formData.num_instructores) : null,
        num_alumnos: formData.num_alumnos ? parseInt(formData.num_alumnos) : null,
        num_escenarios_personalizados: formData.num_escenarios_personalizados ? parseInt(formData.num_escenarios_personalizados) : null
      };
      
      const res = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuoteId(data.quote_id);
        setSuccess(true);
        toast.success('Solicitud enviada correctamente');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al enviar solicitud');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
    setLoading(false);
  };
  
  const validateStep = () => {
    if (step === 1) {
      return formData.nombre_contacto && formData.email_contacto;
    }
    if (step === 2) {
      return formData.nombre_institucion && formData.tipo_institucion && formData.region;
    }
    return true;
  };
  
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Nombre completo *</Label>
          <Input 
            value={formData.nombre_contacto}
            onChange={(e) => handleChange('nombre_contacto', e.target.value)}
            placeholder="Juan Pérez"
          />
        </div>
        <div>
          <Label>Cargo</Label>
          <Input 
            value={formData.cargo}
            onChange={(e) => handleChange('cargo', e.target.value)}
            placeholder="Director de Simulación"
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Email institucional *</Label>
          <Input 
            type="email"
            value={formData.email_contacto}
            onChange={(e) => handleChange('email_contacto', e.target.value)}
            placeholder="juan.perez@institucion.cl"
          />
        </div>
        <div>
          <Label>Teléfono</Label>
          <Input 
            value={formData.telefono_contacto}
            onChange={(e) => handleChange('telefono_contacto', e.target.value)}
            placeholder="+56 9 1234 5678"
          />
        </div>
      </div>
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Nombre de la institución *</Label>
          <Input 
            value={formData.nombre_institucion}
            onChange={(e) => handleChange('nombre_institucion', e.target.value)}
            placeholder="Universidad de Chile"
          />
        </div>
        <div>
          <Label>Tipo de institución *</Label>
          <Select 
            value={formData.tipo_institucion}
            onValueChange={(v) => handleChange('tipo_institucion', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_INSTITUCION.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>RUT institución</Label>
          <Input 
            value={formData.rut_institucion}
            onChange={(e) => handleChange('rut_institucion', e.target.value)}
            placeholder="12.345.678-9"
          />
        </div>
        <div>
          <Label>Sitio web</Label>
          <Input 
            value={formData.sitio_web}
            onChange={(e) => handleChange('sitio_web', e.target.value)}
            placeholder="www.institucion.cl"
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Región *</Label>
          <Select 
            value={formData.region}
            onValueChange={(v) => handleChange('region', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar región..." />
            </SelectTrigger>
            <SelectContent>
              {REGIONES_CHILE.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Comuna</Label>
          <Input 
            value={formData.comuna}
            onChange={(e) => handleChange('comuna', e.target.value)}
            placeholder="Santiago"
          />
        </div>
      </div>
    </div>
  );
  
  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <Label>Plan de interés</Label>
        <Select 
          value={formData.plan_interes}
          onValueChange={(v) => handleChange('plan_interes', v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar plan..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="profesional">Profesional</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="red_distribuidor">Red / Distribuidor</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label>N° usuarios estimado</Label>
          <Input 
            type="number"
            value={formData.num_usuarios_estimado}
            onChange={(e) => handleChange('num_usuarios_estimado', e.target.value)}
            placeholder="50"
          />
        </div>
        <div>
          <Label>N° establecimientos</Label>
          <Input 
            type="number"
            value={formData.num_establecimientos}
            onChange={(e) => handleChange('num_establecimientos', e.target.value)}
            placeholder="2"
          />
        </div>
        <div>
          <Label>N° instructores</Label>
          <Input 
            type="number"
            value={formData.num_instructores}
            onChange={(e) => handleChange('num_instructores', e.target.value)}
            placeholder="5"
          />
        </div>
        <div>
          <Label>N° alumnos</Label>
          <Input 
            type="number"
            value={formData.num_alumnos}
            onChange={(e) => handleChange('num_alumnos', e.target.value)}
            placeholder="40"
          />
        </div>
      </div>
      
      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
        <div className="flex items-center gap-2">
          <Checkbox 
            checked={formData.escenarios_personalizados}
            onCheckedChange={(v) => handleChange('escenarios_personalizados', v)}
          />
          <Label>Requiero escenarios clínicos personalizados</Label>
        </div>
        
        {formData.escenarios_personalizados && (
          <>
            <div>
              <Label>¿Cuántos escenarios personalizados necesita?</Label>
              <Input 
                type="number"
                value={formData.num_escenarios_personalizados}
                onChange={(e) => handleChange('num_escenarios_personalizados', e.target.value)}
                placeholder="3"
              />
            </div>
            <div>
              <Label>Describa brevemente los escenarios requeridos</Label>
              <Textarea 
                value={formData.descripcion_escenarios}
                onChange={(e) => handleChange('descripcion_escenarios', e.target.value)}
                placeholder="Ej: Escenario de paro cardiorrespiratorio en urgencias, manejo de preeclampsia severa..."
                rows={3}
              />
            </div>
          </>
        )}
      </div>
      
      <div>
        <Label>Fecha estimada de inicio</Label>
        <Input 
          type="date"
          value={formData.fecha_inicio_deseada}
          onChange={(e) => handleChange('fecha_inicio_deseada', e.target.value)}
        />
      </div>
      
      <div>
        <Label>Comentarios adicionales</Label>
        <Textarea 
          value={formData.comentarios}
          onChange={(e) => handleChange('comentarios', e.target.value)}
          placeholder="Información adicional que desee compartir..."
          rows={3}
        />
      </div>
      
      <div>
        <Label>¿Cómo conoció SimuClinic?</Label>
        <Select 
          value={formData.como_conocio}
          onValueChange={(v) => handleChange('como_conocio', v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar..." />
          </SelectTrigger>
          <SelectContent>
            {COMO_CONOCIO.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">
        ¡Solicitud enviada!
      </h3>
      <p className="text-slate-600 mb-4">
        Hemos recibido tu solicitud de cotización. Nuestro equipo comercial se pondrá en contacto contigo en las próximas 24-48 horas hábiles.
      </p>
      <p className="text-sm text-slate-500">
        Número de referencia: <code className="px-2 py-1 bg-slate-100 rounded">{quoteId}</code>
      </p>
      {onClose && (
        <Button className="mt-6" onClick={onClose}>
          Cerrar
        </Button>
      )}
    </div>
  );
  
  const content = (
    <div className="space-y-6">
      {!success ? (
        <>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map(s => (
              <React.Fragment key={s}>
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors
                    ${step >= s 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-200 text-slate-600'
                    }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 rounded ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          {/* Step Content */}
          <div className="min-h-[300px]">
            {step === 1 && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Datos de contacto
                </h3>
                {renderStep1()}
              </>
            )}
            {step === 2 && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Información de la institución
                </h3>
                {renderStep2()}
              </>
            )}
            {step === 3 && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Requerimientos
                </h3>
                {renderStep3()}
              </>
            )}
          </div>
          
          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>
                Anterior
              </Button>
            ) : (
              <div />
            )}
            
            {step < 3 ? (
              <Button 
                onClick={() => setStep(s => s + 1)}
                disabled={!validateStep()}
              >
                Siguiente
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading || !validateStep()}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Enviar solicitud
                  </span>
                )}
              </Button>
            )}
          </div>
        </>
      ) : (
        renderSuccess()
      )}
    </div>
  );
  
  if (isModal) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Solicitar Cotización</DialogTitle>
            <DialogDescription>
              Complete el formulario y nos pondremos en contacto con usted
            </DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="container max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Solicitar Cotización</CardTitle>
            <CardDescription>
              Complete el formulario y nuestro equipo comercial se pondrá en contacto con usted
            </CardDescription>
          </CardHeader>
          <CardContent>
            {content}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
